import { PaymentMethodVM, PaymentMethodEntityTypesEnum } from "@propertelligent/client-api";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import Card from "../../../newComponents/card";
import TextButton from "../../../newComponents/buttons/text-button";
import PlaidScreen from "../../../newComponents/PlaidScreen/PlaidScreen";
import AddPlaidAccount from "../../../components/firstTimeUserExperience/AddPlaidAccount";
import {
  QuickstartProvider,
  PlaidAccountDetails,
} from "../../../components/firstTimeUserExperience/Context";

export interface PlaidPaymentAccountModalsProps {
  isAddModalOpen: boolean;
  onAddModalClose: () => void;
  /** Fired by AddPlaidAccount from both the Add and Reconnect modals on a successful Plaid Link. */
  onPlaidSuccess: (plaidData: PlaidAccountDetails) => void;
  plaidEntityConfig: { entityTypeId: PaymentMethodEntityTypesEnum; entityId: number };
  addModalTitle?: string;
  addModalMaxWidth?: number;
  /** Only meaningful when showAddModalPlaidFooter is false (RightSideModal's own default footer). */
  addModalButtonLabel?: string;
  addModalFormId?: string;
  isAddModalDirty?: boolean;
  isAddModalLoading?: boolean;
  /**
   * When false, the Plaid Cancel+Connect footer is suppressed and RightSideModal falls back to its own
   * buttonlabel/formId-driven default footer. Used by the organization-level Payment Accounts page, whose
   * Add modal also serves a legacy ACH/CreditCard edit flow (deprecated payment types, still editable) that
   * is out of scope for this Plaid-only extraction. Defaults to true.
   */
  showAddModalPlaidFooter?: boolean;
  /**
   * Connects an EXISTING non-Plaid payment method (the organization's signup-seeded default placeholder) to
   * Plaid in place: forwarded to QuickstartProvider as plaidReconnectPaymentMethod, so the Plaid link result is
   * PUT onto this row (same id) instead of POSTing a new one. Only meaningful with showAddModalPlaidFooter.
   */
  addModalConnectExistingMethod?: PaymentMethodVM | null;
  /** Label of the Plaid Link button in the Add/Edit modal's footer (QuickstartProvider's plaidButtonLabel). */
  addModalPlaidButtonLabel?: string;
  /** Modal body; defaults to the Plaid-connect screen shared by all three pages. Override for the
   * organization-level page's legacy ACH/CreditCard edit form. */
  addModalChildren?: React.ReactNode;

  isReconnectModalOpen: boolean;
  reconnectPaymentMethod: PaymentMethodVM | null;
  onReconnectModalClose: () => void;
  reconnectModalMaxWidth?: number;
}

/**
 * Shared Add-Payment-Method + Reconnect-Plaid RightSideModal pair, deduplicated out of three
 * near-identical copies: the organization-level Payment Accounts page (./index.tsx), the
 * platform-portal Payment Accounts page (../../../components/platform-portal/payment-accounts),
 * and the organization-user Payment Accounts page (../../../components/my-settings/payment-accounts).
 * All three connect a Plaid receiving account via QuickstartProvider + AddPlaidAccount and reconnect a
 * disconnected one the same way; only the entity type/id sent to Plaid, the modal copy/sizing, and (for
 * the organization-level page only) a legacy ACH/CreditCard edit fallback vary -- captured as props.
 *
 * Both RightSideModal instances only mount AddPlaidAccount while actually open: customFooter is
 * `undefined` (not just CSS-hidden) when closed. RightSideModal keeps children mounted while closed, and
 * an always-mounted AddPlaidAccount would keep re-firing its onPlaidSuccess effect on every re-render --
 * including the re-render its own post-success refetch causes -- flooding the list endpoint (see
 * bug/plaid-link-stuck-loading, guarded by ./index.test.tsx).
 */
const PlaidPaymentAccountModals = ({
  isAddModalOpen,
  onAddModalClose,
  onPlaidSuccess,
  plaidEntityConfig,
  addModalTitle = "Add Payment Account",
  addModalMaxWidth = 550,
  addModalButtonLabel,
  addModalFormId,
  isAddModalDirty = false,
  isAddModalLoading = false,
  showAddModalPlaidFooter = true,
  addModalConnectExistingMethod,
  addModalPlaidButtonLabel,
  addModalChildren,
  isReconnectModalOpen,
  reconnectPaymentMethod,
  onReconnectModalClose,
  reconnectModalMaxWidth = 500,
}: PlaidPaymentAccountModalsProps) => {
  return (
    <>
      <RightSideModal
        isOpen={isReconnectModalOpen}
        onClose={onReconnectModalClose}
        title="Reconnect Plaid Account"
        maxWidth={reconnectModalMaxWidth}
        buttonlabel={undefined}
        formId={undefined}
        customFooter={
          isReconnectModalOpen && reconnectPaymentMethod ? (
            <div className="d-flex justify-content-end gap-2">
              <TextButton label="Cancel" onClick={onReconnectModalClose} />
              <QuickstartProvider
                plaidReconnectPaymentMethod={reconnectPaymentMethod}
                plaidButtonLabel="Reconnect"
              >
                <AddPlaidAccount onPlaidSuccess={onPlaidSuccess} showFinishButton={false} />
              </QuickstartProvider>
            </div>
          ) : undefined
        }
      >
        <Card className="no-bg">
          <PlaidScreen />
        </Card>
      </RightSideModal>

      <RightSideModal
        isOpen={isAddModalOpen}
        onClose={onAddModalClose}
        title={addModalTitle}
        buttonlabel={addModalButtonLabel}
        formId={addModalFormId}
        maxWidth={addModalMaxWidth}
        isDirty={isAddModalDirty}
        isLoading={isAddModalLoading}
        customFooter={
          showAddModalPlaidFooter && isAddModalOpen ? (
            <div className="d-flex justify-content-end gap-2">
              <TextButton label="Cancel" onClick={onAddModalClose} />
              <QuickstartProvider
                plaidEntityConfig={plaidEntityConfig}
                plaidReconnectPaymentMethod={addModalConnectExistingMethod}
                plaidButtonLabel={addModalPlaidButtonLabel}
              >
                <AddPlaidAccount onPlaidSuccess={onPlaidSuccess} showFinishButton={false} />
              </QuickstartProvider>
            </div>
          ) : undefined
        }
      >
        {addModalChildren ?? (
          <Card className="no-bg">
            <PlaidScreen />
          </Card>
        )}
      </RightSideModal>
    </>
  );
};

export default PlaidPaymentAccountModals;
